/**
 * Notification Stack — eintreffende App-Benachrichtigungen (Toast-Mockup).
 *
 * Mockup-/Beweis-Allrounder: Push-Notifications gleiten nacheinander von oben
 * ein und stapeln sich wie auf einem Sperrbildschirm — „Neue Bestellung",
 * „5 neue Anfragen", „★★★★★ Bewertung". Perfekt, um Aktivität/Traction zu
 * zeigen, oder als lebendiger Platzfüller. Optional ein kleiner Bell-Badge.
 *
 * Items-Format:  "Titel | Text | iconKey"
 *   z. B. "Neue Bestellung | Tisch für 4 · 19:30 Uhr | shop"
 *
 * Inputs:
 *   - text         → optionale Headline über dem Stapel
 *   - extras.appName → App-Name im Notification-Header (Default: Markenname)
 *   - extras.time    → Zeitstempel-Text rechts (Default "jetzt")
 *
 * Motion: jede Karte fliegt von oben ein (y + scale + opacity), die darunter
 * liegenden rücken minimal nach; ein Bell-Badge poppt bei jeder neuen Karte.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandName, clipOutroFade, combineSceneFragments, escapeHtml,
  gsapSetLine, makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "./_mockHelpers.js";

export const notificationStack: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary;
  const pink = themeColors.accent;
  const palette = [accent, violet, pink, accent, violet];
  const isVertical = ctx.format === "9:16";

  const headline = inputs.text ?? "";
  const appName = String(inputs.extras?.appName ?? brandName);
  const timeText = String(inputs.extras?.time ?? "jetzt");

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "Neue Bestellung | Tisch für 4 · 19:30 Uhr | shop",
        "5 neue Anfragen | Kund:innen in deiner Nähe | users",
        "★★★★★ Bewertung | „Schneller, lokaler Service!“ | star",
        "Zahlung erhalten | 48,00 € über Stripe | euro",
      ]
  ).slice(0, isVertical ? 4 : 5);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const stackId = `${clipPrefix}-stack`;
  const cardIds = items.map((_, i) => `${clipPrefix}-card-${i}`);
  const bellIds = items.map((_, i) => `${clipPrefix}-bell-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  const titlePx = isVertical ? sm * 0.74 : md * 0.46;
  const bodyPx = isVertical ? sm * 0.58 : md * 0.34;
  const metaPx = isVertical ? sm * 0.46 : sm * 0.5;
  const iconBox = isVertical ? 56 : 62;
  const cardMaxW = isVertical ? 860 : 1080;

  const parsed = items.map((raw, i) => {
    const [title, body, iconRaw] = splitItem(raw);
    return {
      title: (title ?? "").trim(),
      body: (body ?? "").trim(),
      icon: (iconRaw ?? "").trim() || "message",
      color: palette[i % palette.length] ?? accent,
    };
  });

  function card(i: number, it: (typeof parsed)[number]): string {
    return `<div id="${cardIds[i]}" style="position:relative;display:flex;align-items:center;gap:${isVertical ? 16 : 20}px;width:100%;max-width:${cardMaxW}px;padding:${isVertical ? "18px 20px" : "22px 26px"};border-radius:24px;background:linear-gradient(160deg, ${withAlpha(themeColors.fg, 0.12)} 0%, ${withAlpha(themeColors.bg, 0.66)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.16)};box-shadow:0 18px 44px rgba(0,0,0,0.4), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.12)};backdrop-filter:blur(8px);will-change:transform,opacity;">
      <div style="position:relative;width:${iconBox}px;height:${iconBox}px;flex-shrink:0;border-radius:16px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 30% 30%, ${withAlpha(it.color, 0.7)} 0%, ${withAlpha(it.color, 0.25)} 70%);border:1px solid ${withAlpha(it.color, 0.6)};box-shadow:0 0 22px ${withAlpha(it.color, 0.5)};color:${themeColors.fg};">
        ${lucideIcon(it.icon, Math.round(iconBox * 0.5), themeColors.fg, 2.2)}
        <div id="${bellIds[i]}" style="position:absolute;top:-6px;right:-6px;width:18px;height:18px;border-radius:9999px;background:${pink};border:2px solid ${themeColors.bg};box-shadow:0 0 10px ${withAlpha(pink, 0.8)};"></div>
      </div>
      <div style="display:flex;flex-direction:column;gap:4px;flex:1;min-width:0;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
          <span style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titlePx}px;letter-spacing:-0.4px;color:${themeColors.fg};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(it.title)}</span>
          <span style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${metaPx}px;color:${withAlpha(themeColors.fg, 0.5)};white-space:nowrap;">${escapeHtml(timeText)}</span>
        </div>
        ${it.body ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${bodyPx}px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.74)};">${escapeHtml(it.body)}</div>` : ""}
      </div>
    </div>`;
  }

  const cardsHtml = parsed.map((it, i) => card(i, it)).join("");
  const appPx = isVertical ? sm * 0.56 : sm * 0.6;
  const headPx = isVertical ? lg * 0.86 : lg * 0.8;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 28 : 32}px;padding:${isVertical ? "80px 48px" : "92px 130px"};overflow:hidden;">
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.04;letter-spacing:-1.6px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 860 : 1200}px;text-shadow:0 0 50px ${withAlpha(accent, 0.35)};">${escapeHtml(headline)}</div>` : ""}
    <div style="display:flex;align-items:center;gap:10px;color:${withAlpha(themeColors.fg, 0.6)};font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${appPx}px;letter-spacing:2px;text-transform:uppercase;">
      <span style="width:10px;height:10px;border-radius:9999px;background:${accent};box-shadow:0 0 12px ${accent};"></span>
      <span>${escapeHtml(appName)}</span>
    </div>
    <div id="${stackId}" style="display:flex;flex-direction:column;gap:${isVertical ? 14 : 16}px;width:100%;align-items:center;">
      ${cardsHtml}
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 20 }) : "",
    ...cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: -40, scale: 0.9, transformOrigin: "50% 0%" })),
    ...bellIds.map((id) => gsapSetLine(`#${id}`, { scale: 0, transformOrigin: "50% 50%" })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.15));

  const cardStart = headline ? 0.55 : 0.3;
  const entryWindow = Math.min(2.2, Math.max(0.9, clipDurationSec * 0.6));
  const step = entryWindow / Math.max(parsed.length, 1);

  parsed.forEach((_, i) => {
    const at = cardStart + i * step;
    tweens.push(t.to(`#${cardIds[i]}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.7)\"" }, at));
    tweens.push(t.to(`#${bellIds[i]}`, { scale: 1, duration: 0.34, ease: "raw:\"back.out(3)\"" }, at + 0.18));
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
